import { Text, ReactTable, Box } from '@wegroup/design-system';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Cell } from 'react-table';
import { toEuro } from '../../../../../../sales-conversation/src/utils/stringUtils';
import { CompanyLease } from '../../../../types/CompanyScanTypes';
import InfoPopover from '../common/InfoPopover';
import TableActionsMenu from '../common/TableActionsMenu';
import TableFiller from '../common/TableFiller';

interface Props {
  companyLeases: CompanyLease[];
  onEdit: (companyLease: CompanyLease, index: number) => void;
  onRemove: (index: number) => void;
}

const CompanyLeaseTable: React.FC<React.PropsWithChildren<Props>> = ({
  companyLeases,
  onEdit,
  onRemove,
}) => {
  const { t } = useTranslation();

  const renderTypeCell = (cell: Cell) => {
    const { type } = cell?.row?.original as CompanyLease;

    return (
      <Text color="text.gray">
        {t(`FLOWS_COMPANY-SCAN_LEASE-ENUM_${type}`)}
      </Text>
    );
  };

  const renderDurationCell = (cell: Cell) => {
    const { duration } = cell?.row?.original as CompanyLease;

    return <Text color="text.gray">{`${duration} ${t('MONTHS')}`}</Text>;
  };

  const renderAmountCell = (cell: Cell) => {
    const { amount } = cell?.row?.original as CompanyLease;

    return <Text color="text.gray">{toEuro(amount)}</Text>;
  };

  const renderPurposeCell = (cell: Cell) => {
    const { purpose } = cell?.row?.original as CompanyLease;

    if (!purpose) return '-';

    return (
      <InfoPopover>
        <Text>{purpose}</Text>
      </InfoPopover>
    );
  };

  const renderLeasingCompanyDetailCell = (cell: Cell) => {
    const { leasing_company } = cell?.row?.original as CompanyLease;

    if (!leasing_company) return '-';

    return <Text color="text.gray">{leasing_company}</Text>;
  };

  const renderActionMenuCell = (cell: Cell) => {
    const companyLease = cell?.row?.original as CompanyLease;
    const index = cell?.row?.index;

    return (
      <TableActionsMenu
        onEdit={() => onEdit(companyLease, index)}
        onRemove={() => onRemove(index)}
      />
    );
  };

  const columns = useMemo(
    () => [
      {
        Header: t('FLOWS_COMPANY-SCAN_FINANCIAL-FORM_TYPE'),
        accessor: 'type',
        Cell: renderTypeCell,
        isSticky: true,
      },
      {
        Header: t('FLOWS_COMPANY-SCAN_FINANCIAL-FORM_DURATION'),
        accessor: 'duration',
        Cell: renderDurationCell,
      },
      {
        Header: t('FLOWS_COMPANY-SCAN_FINANCIAL-FORM_AMOUNT'),
        accessor: 'amount',
        Cell: renderAmountCell,
      },
      {
        Header: t('FLOWS_COMPANY-SCAN_FINANCIAL-FORM_PURPOSE'),
        accessor: 'purpose',
        Cell: renderPurposeCell,
        disableSortBy: true,
      },
      {
        Header: t('FLOWS_COMPANY-SCAN_FINANCIAL-FORM_LEASING-COMPANY'),
        accessor: 'details',
        Cell: renderLeasingCompanyDetailCell,
      },
      {
        Header: t('ACTIONS'),
        id: 'actions',
        Cell: renderActionMenuCell,
        disableSortBy: true,
      },
    ],
    [companyLeases],
  );

  return (
    <Box>
      <ReactTable
        data={companyLeases}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        columns={columns as any}
      />
      {companyLeases.length === 0 && (
        <TableFiller>
          <Text>{t('FLOWS_COMPANY-SCAN_COMPANY-LEASE-INPUT_EMPTY-TABLE')}</Text>
        </TableFiller>
      )}
    </Box>
  );
};

export default CompanyLeaseTable;
